import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import axios from "axios";
import { FaSave, FaSpinner, FaUserEdit } from "react-icons/fa";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import { getPayrollModalStyles } from "./payrollMonthlyShared";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const SALARY_CIRCLE_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const WEEK_OFF_DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

//Created By Cursor
function normalizeJoiningDateForInput(value) {
  if (!value) return "";
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

//Created By Cursor
function buildFormFromStaff(staff) {
  if (!staff) {
    return {
      salary_type: "",
      salary: "",
      salary_circle: "",
      working_hour: "8",
      joining_date: "",
      week_off: "Sunday",
    };
  }
  return {
    salary_type: staff.salary_type || "",
    salary:
      staff.salary != null && staff.salary !== ""
        ? String(staff.salary)
        : staff.basic_salary != null && staff.basic_salary !== ""
        ? String(staff.basic_salary)
        : "",
    salary_circle:
      staff.salary_circle != null && staff.salary_circle !== ""
        ? String(staff.salary_circle)
        : "",
    working_hour:
      staff.working_hour != null && staff.working_hour !== ""
        ? String(staff.working_hour)
        : "8",
    joining_date: normalizeJoiningDateForInput(staff.joining_date),
    week_off: staff.week_off || "Sunday",
  };
}

//Created By Cursor
function staffInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

const MdlEditPayRollStaffDetails = ({
  show = false,
  actionClose,
  staffRecord = null,
  sessionId = "",
  onSaved,
}) => {
  const [modalShow, setModalShow] = useState(show);
  const [form, setForm] = useState(() => buildFormFromStaff(staffRecord));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const modalStyles = useMemo(() => getPayrollModalStyles(), []);

  useEffect(() => {
    setModalShow(show);
  }, [show]);

  useEffect(() => {
    if (staffRecord && show) {
      setForm(buildFormFromStaff(staffRecord));
      setSaveError("");
    }
  }, [staffRecord, show]);

  const staffLabel = staffRecord?.staff_name || "Staff";

  //Created By Cursor
  const handleClose = useCallback(() => {
    setModalShow(false);
    if (actionClose) actionClose();
  }, [actionClose]);

  //Created By Cursor
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setSaveError("");
  }, []);

  //Created By Cursor
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!staffRecord?.staff_id || !sessionId) {
        setSaveError("Missing staff or session.");
        return;
      }

      const salaryRaw = form.salary;
      const hasSalary =
        salaryRaw !== "" &&
        salaryRaw != null &&
        !Number.isNaN(Number(salaryRaw));
      const joiningDate = String(form.joining_date || "").trim();

      if (!form.salary_type || !hasSalary || !joiningDate) {
        setSaveError("Salary type, salary, and joining date are required.");
        return;
      }

      setSaving(true);
      setSaveError("");

      try {
        const SCHOOL_CODE =
          staffRecord.school_code || SessionManager.getSchoolCode();
        const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}UpdateStaffPayRollDetails.php`;

        const payload = {
          school_code: SCHOOL_CODE,
          session_id: sessionId,
          staff_id: staffRecord.staff_id,
          salary_type: form.salary_type,
          salary: form.salary,
          salary_circle: form.salary_circle || "",
          working_hour: form.working_hour || "8",
          joining_date: joiningDate,
          week_off: form.week_off || "Sunday",
        };

        const response = await axios.get(URL, { params: payload });

        if (response.data?.api_status === "OK") {
          myToast.notify_success(
            response.data?.api_remark || "Payroll details updated."
          );
          if (onSaved) {
            await onSaved({
              ...staffRecord,
              ...payload,
              basic_salary: payload.salary,
              salary: payload.salary,
            });
          }
          handleClose();
        } else {
          const msg =
            response.data?.api_remark || "Could not update payroll details.";
          setSaveError(msg);
          myToast.notify_error(msg);
        }
      } catch (err) {
        console.error("UpdateStaffPayRollDetails error:", err);
        const raw = err?.response?.data;
        let msg = "Failed to update payroll details. Please try again.";
        if (typeof raw === "string" && raw.trim()) {
          msg = raw.replace(/<[^>]+>/g, "").slice(0, 240);
        } else if (raw?.api_remark) {
          msg = raw.api_remark;
        } else if (err?.message) {
          msg = err.message;
        }
        setSaveError(msg);
        myToast.notify_error(msg);
      } finally {
        setSaving(false);
      }
    },
    [staffRecord, sessionId, form, onSaved, handleClose]
  );

  return (
    <Modal
      show={modalShow}
      onHide={handleClose}
      centered
      size="lg"
      animation={false}
      backdrop="static"
      backdropClassName="custom-backdrop"
      dialogClassName="pp-edit-payroll-mdl-dialog"
      contentClassName="custom-modal-content payroll-mdl pp-edit-payroll-mdl"
      aria-labelledby="edit-payroll-staff-title"
    >
      <style>{modalStyles}</style>
      <div className="pp-edit-payroll-mdl__shell">
        <Modal.Header closeButton className="pp-edit-payroll-mdl__header border-0">
          <div className="pp-edit-payroll-mdl__hero">
            <div className="pp-edit-payroll-mdl__avatar" aria-hidden>
              {staffInitials(staffLabel)}
            </div>
            <div className="pp-edit-payroll-mdl__hero-text min-w-0">
              <span className="pp-edit-payroll-mdl__eyebrow">
                <FaUserEdit aria-hidden />
                Edit payroll
              </span>
              <Modal.Title
                id="edit-payroll-staff-title"
                className="pp-edit-payroll-mdl__title text-truncate"
              >
                {staffLabel}
              </Modal.Title>
            </div>
          </div>
        </Modal.Header>

        <Form
          onSubmit={handleSubmit}
          className="pp-edit-payroll-mdl__form"
        >
          <Modal.Body className="pp-edit-payroll-mdl__body">
            {saveError ? (
              <div
                className="pp-edit-payroll-mdl__alert"
                role="alert"
              >
                {saveError}
              </div>
            ) : null}

            <p className="pp-edit-payroll-mdl__hint">
              Update master payroll settings for this staff member.
            </p>

            <div className="pp-edit-payroll-staff-form-panel">
              <div className="pp-edit-payroll-mdl__section-label">
                Compensation
              </div>
              <div className="row g-3 pp-edit-payroll-staff-form">
                <div className="col-12 col-md-6">
                  <Form.Label className="pp-edit-payroll-staff-form__label">
                    Salary type <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="salary_type"
                    value={form.salary_type}
                    onChange={handleChange}
                    required
                    disabled={saving}
                    className="pp-edit-payroll-staff-form__control"
                  >
                    <option value="">Select…</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Daily">Daily</option>
                    <option value="Hourly">Hourly</option>
                  </Form.Select>
                </div>
                <div className="col-12 col-md-6">
                  <Form.Label className="pp-edit-payroll-staff-form__label">
                    Basic salary (₹) <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="salary"
                    value={form.salary}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    required
                    disabled={saving}
                    className="pp-edit-payroll-staff-form__control"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="pp-edit-payroll-mdl__section-label mt-3">
                Schedule
              </div>
              <div className="row g-3 pp-edit-payroll-staff-form">
                <div className="col-12 col-md-6">
                  <Form.Label className="pp-edit-payroll-staff-form__label">
                    Salary circle (day)
                  </Form.Label>
                  <Form.Select
                    name="salary_circle"
                    value={form.salary_circle}
                    onChange={handleChange}
                    disabled={saving}
                    className="pp-edit-payroll-staff-form__control"
                  >
                    <option value="">Not set</option>
                    {SALARY_CIRCLE_DAYS.map((d) => (
                      <option key={d} value={String(d)}>
                        {d}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-12 col-md-6">
                  <Form.Label className="pp-edit-payroll-staff-form__label">
                    Working hours
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="working_hour"
                    value={form.working_hour}
                    onChange={handleChange}
                    min="1"
                    max="24"
                    disabled={saving}
                    className="pp-edit-payroll-staff-form__control"
                    placeholder="8"
                  />
                </div>
                <div className="col-12 col-md-6">
                  <Form.Label className="pp-edit-payroll-staff-form__label">
                    Joining date <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="joining_date"
                    value={form.joining_date}
                    onChange={handleChange}
                    required
                    disabled={saving}
                    className="pp-edit-payroll-staff-form__control"
                  />
                </div>
                <div className="col-12 col-md-6">
                  <Form.Label className="pp-edit-payroll-staff-form__label">
                    Week off
                  </Form.Label>
                  <Form.Select
                    name="week_off"
                    value={form.week_off}
                    onChange={handleChange}
                    disabled={saving}
                    className="pp-edit-payroll-staff-form__control"
                  >
                    {WEEK_OFF_DAYS.map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer className="pp-edit-payroll-mdl__footer border-0 d-flex flex-wrap justify-content-between align-items-center gap-2">
            <Button
              type="button"
              variant="light"
              className="pp-edit-payroll-mdl-cancel fw-semibold"
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="pp-footer-primary pp-edit-payroll-mdl-save"
              disabled={saving}
            >
              {saving ? (
                <>
                  <FaSpinner className="me-2 payroll-mdl-spinner" />
                  Saving…
                </>
              ) : (
                <>
                  <FaSave className="me-2" aria-hidden />
                  Save changes
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </div>
    </Modal>
  );
};

export default MdlEditPayRollStaffDetails;
